//"use client"

import { useEffect, useRef, useState } from "react"
import header012 from "./../../../Assets/header12.Webp"
import mainVideo from "./../../../Assets/mainvideo.mp4"
import heroPoster from "./../../../Assets/hero-poster.webp"
import "./Header11.css"

function Header11({ appointmentRef }) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const backgroundVideoRef = useRef(null)
  const headerRef = useRef(null)
  const contentRef = useRef(null)

  const scrollToAppointment = () => {
    if (appointmentRef.current) {
      appointmentRef.current.scrollIntoView({ behavior: "instant" })
    }
  }

  useEffect(() => {
    // The entrance animations no longer wait for the video: the poster is shown straight away and the text appears
    // after a short delay, so the first paint and LCP do not depend on a 4.6 MB download.
    const showTimer = setTimeout(() => setIsVisible(true), 300)

    const video = backgroundVideoRef.current
    let startTimer
    let onWindowLoad
    let onInteract
    if (video) {
      video.onloadeddata = () => {
        video.classList.add("loaded")
        setIsLoaded(true)
      }

      // The background video is decoration. It is fetched only after the page has finished loading, and not at all on
      // data-saver connections, reduced-motion preferences, small screens, or while scripts/prerender.js takes its
      // snapshot (which keeps the prerendered markup identical to the first client render).
      const conn = navigator.connection || {}
      const skipVideo =
        window.__PRERENDER__ ||
        conn.saveData ||
        /(^|-)2g$/.test(conn.effectiveType || "") ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        window.matchMedia("(max-width: 767px)").matches

      if (!skipVideo) {
        let started = false
        const playNow = () => {
          if (started) return
          started = true
          clearTimeout(startTimer)
          if (onInteract) INTERACTIONS.forEach((e) => window.removeEventListener(e, onInteract))
          video.src = mainVideo
          video.load()
          const played = video.play()
          if (played && played.catch) played.catch(() => {})
        }
        // Start on the visitor's first interaction, or 8 s after the page has loaded if they just sit there. Not
        // earlier: a video frame that paints a few seconds in would otherwise become the page's Largest Contentful
        // Paint (it is bigger than the heading text) and push LCP out by the time the 4.6 MB file takes to arrive.
        const INTERACTIONS = ["pointerdown", "keydown", "scroll", "touchstart"]
        onInteract = playNow
        INTERACTIONS.forEach((e) => window.addEventListener(e, onInteract, { passive: true }))
        const armFallback = () => {
          startTimer = setTimeout(playNow, 8000)
        }
        if (document.readyState === "complete") armFallback()
        else {
          onWindowLoad = armFallback
          window.addEventListener("load", onWindowLoad, { once: true })
        }
      }
    }

    // Intersection Observer for scroll-triggered animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in")
          }
        })
      },
      { threshold: 0.1 },
    )

    if (headerRef.current) {
      observer.observe(headerRef.current)
    }

    // Parallax effect on scroll
    const handleScroll = () => {
      const scrolled = window.pageYOffset
      const parallax = headerRef.current
      if (parallax) {
        const speed = scrolled * 0.5
        parallax.style.transform = `translateY(${speed}px)`
      }
    }

    window.addEventListener("scroll", handleScroll)

    return () => {
      clearTimeout(showTimer)
      clearTimeout(startTimer)
      if (onWindowLoad) window.removeEventListener("load", onWindowLoad)
      if (onInteract) ["pointerdown", "keydown", "scroll", "touchstart"].forEach((e) => window.removeEventListener(e, onInteract))
      observer.disconnect()
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <>
      <div className="hd-layout1" ref={headerRef}>
        <div className="hd-image-container">
          <img src={heroPoster} alt="" className="hd-poster-img" width="1280" height="720" fetchpriority="high" decoding="async" />
          <video
            ref={backgroundVideoRef}
            preload="none"
            className="hd-background-video"
            loop
            muted
            playsInline
          >
            Your browser does not support the video tag.
          </video>
          <div className="video-overlay"></div>
        </div>
        <div className={`hd-layout3 ${isVisible ? "content-visible" : ""}`} ref={contentRef}>
          <div className="hd-layout4 animate-slide-right">
            <div className="hd-layout8 animate-expand"></div>
            <div className="hd-layout9">
              <p className="hd-content1 animate-fade-in">Solutions for your businesses</p>
            </div>
          </div>
          <div className="hd-layout5 animate-slide-up">
            <div className="hd-layout10 animate-grow"></div>
            <div className="hd-layout11">
              <h1 className="hd-content3">
                <span className="typing-effect animate-type-in">Custom Software</span>{" "}
                <br />
                <span className="ampersand animate-bounce-in">&amp;</span>{" "}
                <span className="typing-effect1 animate-type-in-delayed">AI Solutions</span>
              </h1>
            </div>
          </div>
          <div className="hd-layout6 animate-fade-up">
            <p className="hd-content2">
              We provide end-to-end technology solutions that drive innovation, boost efficiency, and help you stay
              ahead in the digital world.
            </p>
          </div>
          <div className="hd-layout7 animate-slide-up-delayed">
            <div className="hd-layout12">
              <button
                type="button"
                className="Estimate animate-button"
                onClick={scrollToAppointment}
                aria-label="Estimate your project"
              >
                <span className="button-text">Estimate your Project</span>
                <div className="button-ripple"></div>
              </button>
            </div>
            <div className="hd-layout13 animate-float">
              <img src={header012 || "/placeholder.svg"} alt="" className="hd-layout14" width="109" height="64" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Header11
